import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  limit,
  onSnapshot,
  getDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { createNotification } from './notificationService';

const RACE_CARDS_COLLECTION = 'raceCards';

// Cache for active race cards
let unsubscribeActiveCards = null;

// Cache for user race cards
const userCardsCache = new Map();
const userCardUnsubscribers = new Map();

export const createRaceCard = async (raceCardData, userId) => {
  try {
    // Check if user already has a card of the same type
    const existingCardQuery = query(
      collection(db, RACE_CARDS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    const existingCards = await getDocs(existingCardQuery);
    
    // Check if user already has a card of this type
    const cardType = raceCardData.cardType;
    const existingCardOfType = existingCards.docs.find(doc => doc.data().cardType === cardType);
    
    if (existingCardOfType) {
      const typeNames = {
        'career': '2 Player Career',
        'unranked': 'Unranked Lobby',
        'reserve': 'Reserve a Race',
        'commentary': 'Commentary Role',
        'leagueStartup': 'League Startup'
      };
      throw new Error(`You already have a ${typeNames[cardType] || cardType} card. You can have one of each type (Career, Unranked, Reserve, Commentary, League Startup).`);
    }

    // Add user ID to the race card data
    const raceCardWithUser = {
      ...raceCardData,
      userId,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, RACE_CARDS_COLLECTION), raceCardWithUser);
    return { id: docRef.id, ...raceCardWithUser };
  } catch (error) {
    console.error('Error creating race card:', error);
    throw error;
  }
};

export const getActiveRaceCards = (callback) => {
  try {
    // Clear any existing cache and listeners
    if (unsubscribeActiveCards) {
      unsubscribeActiveCards();
      unsubscribeActiveCards = null;
    }

    console.log('Setting up query for active race cards...');
    
    // Simple query to get all active cards
    const q = query(
      collection(db, RACE_CARDS_COLLECTION),
      where('status', '==', 'active')
    );

    console.log('Query created:', q);

    // Set up the listener with error handling
    unsubscribeActiveCards = onSnapshot(q, 
      async (querySnapshot) => {
        try {
          console.log('Snapshot received, empty?', querySnapshot.empty);
          console.log('Number of docs:', querySnapshot.docs.length);
          
          const cards = querySnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Card data:', { id: doc.id, ...data });
            return {
              id: doc.id,
              ...data
            };
          });
          
          // Filter out cards that have accepted applications
          const filteredCards = [];
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 28); // 4 weeks ago
          const oneDayAgo = new Date();
          oneDayAgo.setDate(oneDayAgo.getDate() - 1); // 24 hours ago
          
          for (const card of cards) {
            try {
              // Check if card has a valid userId
              if (!card.userId) {
                console.log('Filtering out card without userId:', card.id);
                continue;
              }
              
              // Check if card is expired
              if (card.createdAt) {
                if (card.cardType === 'unranked' && card.raceDate) {
                  // For unranked cards, expire one day after the lobby date
                  const lobbyDate = card.raceDate.toDate();
                  const expiryDate = new Date(lobbyDate);
                  expiryDate.setDate(expiryDate.getDate() + 1); // One day after lobby
                  
                  if (new Date() > expiryDate) {
                    console.log('Deleting expired unranked card:', card.id, 'lobby date:', lobbyDate, 'expired after:', expiryDate);
                    try {
                      await deleteDoc(doc(db, RACE_CARDS_COLLECTION, card.id));
                      console.log('Successfully deleted expired unranked card:', card.id);
                    } catch (deleteError) {
                      console.error('Error deleting expired unranked card:', card.id, deleteError);
                    }
                    continue; // Skip this card
                  }
                } else if (card.cardType === 'reserve' && card.raceDate) {
                  // For reserve cards, expire one day after the race date
                  const raceDate = card.raceDate.toDate();
                  const expiryDate = new Date(raceDate);
                  expiryDate.setDate(expiryDate.getDate() + 1); // One day after race
                  
                  if (new Date() > expiryDate) {
                    console.log('Deleting expired reserve card:', card.id, 'race date:', raceDate, 'expired after:', expiryDate);
                    try {
                      await deleteDoc(doc(db, RACE_CARDS_COLLECTION, card.id));
                      console.log('Successfully deleted expired reserve card:', card.id);
                    } catch (deleteError) {
                      console.error('Error deleting expired reserve card:', card.id, deleteError);
                    }
                    continue; // Skip this card
                  }
                } else if (card.cardType === 'commentary' && card.raceDate) {
                  // For commentary cards, expire one day after the selected date
                  const commsDate = card.raceDate.toDate();
                  const expiryDate = new Date(commsDate);
                  expiryDate.setDate(expiryDate.getDate() + 1); // One day after commentary date
                  
                  if (new Date() > expiryDate) {
                    console.log('Deleting expired commentary card:', card.id, 'date:', commsDate, 'expired after:', expiryDate);
                    try {
                      await deleteDoc(doc(db, RACE_CARDS_COLLECTION, card.id));
                      console.log('Successfully deleted expired commentary card:', card.id);
                    } catch (deleteError) {
                      console.error('Error deleting expired commentary card:', card.id, deleteError);
                    }
                    continue; // Skip this card
                  }
                } else if (card.createdAt.toDate() < twoWeeksAgo) {
                  console.log('Deleting expired card:', card.id, 'created:', card.createdAt.toDate());
                  try {
                    await deleteDoc(doc(db, RACE_CARDS_COLLECTION, card.id));
                    console.log('Successfully deleted expired card:', card.id);
                  } catch (deleteError) {
                    console.error('Error deleting expired card:', card.id, deleteError);
                  }
                  continue; // Skip this card
                }
              }
              
              // Check how many accepted applications this card has
              let maxApplications = 5;
              if (card.cardType === 'leagueStartup') {
                maxApplications = 60;
              } else if (card.cardType === 'unranked' || card.cardType === 'reserve') {
                maxApplications = 22;
              }
              const applicationsQuery = query(
                collection(db, 'raceCardApplications'),
                where('raceCardId', '==', card.id),
                where('status', '==', 'accepted')
              );
              const applicationsSnapshot = await getDocs(applicationsQuery);
              const acceptedCount = applicationsSnapshot.size;
              // For career/commentary: remove if any accepted. For unranked/reserve/leagueStartup: remove only if max accepted.
              if (
                ((card.cardType === 'career' || card.cardType === 'commentary') && acceptedCount > 0) ||
                ((card.cardType === 'unranked' || card.cardType === 'reserve' || card.cardType === 'leagueStartup') && acceptedCount >= maxApplications)
              ) {
                console.log('Filtering out card with max accepted applications:', card.id);
                continue;
              }
              filteredCards.push(card);
            } catch (error) {
              console.error('Error checking applications for card:', card.id, error);
              // If there's an error checking applications, include the card to be safe
              filteredCards.push(card);
            }
          }
          
          console.log('Processed cards:', filteredCards);
          
          // Sort cards by creation date (newest first)
          const sortedCards = filteredCards.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return b.createdAt.toDate() - a.createdAt.toDate();
            }
            // If one card doesn't have createdAt, put it at the end
            if (a.createdAt && !b.createdAt) return -1;
            if (!a.createdAt && b.createdAt) return 1;
            // If neither has createdAt, maintain original order
            return 0;
          });
          
          callback(sortedCards);
        } catch (error) {
          console.error('Error processing race cards:', error);
          callback([]);
        }
      }, 
      (error) => {
        console.error('Error in active race cards listener:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        callback([]);
      }
    );

    return () => {
      if (unsubscribeActiveCards) {
        unsubscribeActiveCards();
        unsubscribeActiveCards = null;
      }
    };
  } catch (error) {
    console.error('Error setting up active race cards listener:', error);
    callback([]);
    return () => {};
  }
};

export const getUserRaceCards = (userId, callback) => {
  try {
    // Always clean up any existing listener for this user
    const existingUnsubscribe = userCardUnsubscribers.get(userId);
    if (existingUnsubscribe) {
      existingUnsubscribe();
      userCardUnsubscribers.delete(userId);
    }
    userCardsCache.delete(userId);

    const q = query(
      collection(db, RACE_CARDS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );

    // Set up the listener
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (querySnapshot.empty) {
        userCardsCache.set(userId, []);
        callback([]);
      } else {
        const cards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort cards by creation date (newest first)
        const sortedCards = cards.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.toDate() - a.createdAt.toDate();
          }
          // If one card doesn't have createdAt, put it at the end
          if (a.createdAt && !b.createdAt) return -1;
          if (!a.createdAt && b.createdAt) return 1;
          // If neither has createdAt, maintain original order
          return 0;
        });
        
        userCardsCache.set(userId, sortedCards);
        callback(sortedCards);
      }
    }, (error) => {
      console.error('Error in user race cards listener:', error);
      callback([]);
    });

    userCardUnsubscribers.set(userId, unsubscribe);

    return () => {
      const unsubscribe = userCardUnsubscribers.get(userId);
      if (unsubscribe) {
        unsubscribe();
        userCardUnsubscribers.delete(userId);
        userCardsCache.delete(userId);
      }
    };
  } catch (error) {
    console.error('Error setting up user race cards listener:', error);
    callback([]);
    return () => {};
  }
};

// Keep the old function name for backward compatibility
export const getUserRaceCard = (userId, callback) => {
  getUserRaceCards(userId, (cards) => {
    // Return the first card or null if no cards
    callback(cards.length > 0 ? cards[0] : null);
  });
};

export const updateRaceCard = async (raceCardId, updates) => {
  try {
    const raceCardRef = doc(db, RACE_CARDS_COLLECTION, raceCardId);
    await updateDoc(raceCardRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating race card:', error);
    throw error;
  }
};

export const deleteRaceCard = async (raceCardId) => {
  try {
    await deleteDoc(doc(db, RACE_CARDS_COLLECTION, raceCardId));
  } catch (error) {
    console.error('Error deleting race card:', error);
    throw error;
  }
};

export const clearUserCardsCache = (userId) => {
  if (userCardsCache.has(userId)) {
    userCardsCache.delete(userId);
  }
  const unsubscribe = userCardUnsubscribers.get(userId);
  if (unsubscribe) {
    unsubscribe();
    userCardUnsubscribers.delete(userId);
  }
};

export const deactivateRaceCard = async (raceCardId) => {
  try {
    const raceCardRef = doc(db, RACE_CARDS_COLLECTION, raceCardId);
    await updateDoc(raceCardRef, {
      status: 'inactive',
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error deactivating race card:', error);
    throw error;
  }
};

// Cleanup function to be called when the app is unmounted
export const cleanupRaceCardListeners = () => {
  if (unsubscribeActiveCards) {
    unsubscribeActiveCards();
    unsubscribeActiveCards = null;
  }

  // Cleanup all user card listeners
  userCardUnsubscribers.forEach(unsubscribe => unsubscribe());
  userCardUnsubscribers.clear();
  userCardsCache.clear();
};

// Add this function at the end of the file, before the last export
export const cleanupAllListeners = () => {
  // Cleanup active cards listener
  if (unsubscribeActiveCards) {
    unsubscribeActiveCards();
    unsubscribeActiveCards = null;
  }

  // Cleanup all user card listeners
  userCardUnsubscribers.forEach(unsubscribe => unsubscribe());
  userCardUnsubscribers.clear();
  userCardsCache.clear();
};

// Race Card Applications
export const applyToRaceCard = async (raceCardId, applicantId, applicantEaId) => {
  try {
    // Check if user has already applied to this race card
    const existingApplicationQuery = query(
      collection(db, 'raceCardApplications'),
      where('raceCardId', '==', raceCardId),
      where('applicantId', '==', applicantId),
      limit(1)
    );
    const existingApplications = await getDocs(existingApplicationQuery);
    
    if (!existingApplications.empty) {
      throw new Error('You have already applied to this race card.');
    }

    // Get the race card to get the creator's EA ID and card data
    const raceCardRef = doc(db, RACE_CARDS_COLLECTION, raceCardId);
    const raceCardDoc = await getDoc(raceCardRef);
    
    if (!raceCardDoc.exists()) {
      throw new Error('Race card not found.');
    }
    
    const raceCardData = raceCardDoc.data();
    const cardCreatorEaId = raceCardData.createdByEaId || 'Unknown';

    // Store essential card data in the application for display purposes
    const cardData = {
      title: raceCardData.title,
      cardType: raceCardData.cardType,
      gameVersion: raceCardData.gameVersion,
      teamPreference: raceCardData.teamPreference,
      aiLevel: raceCardData.aiLevel,
      rivalryType: raceCardData.rivalryType,
      raceDistance: raceCardData.raceDistance,
      timezone: raceCardData.timezone,
      timeOfDay: raceCardData.timeOfDay,
      frequency: raceCardData.frequency,
      preferredDays: raceCardData.preferredDays,
      // Unranked specific fields
      carType: raceCardData.carType,
      qualifyingPreference: raceCardData.qualifyingPreference,
      damage: raceCardData.damage,
      estimatedStartTime: raceCardData.estimatedStartTime,
      runTime: raceCardData.runTime,
      // Reserve specific fields
      track: raceCardData.track,
      estAiLevel: raceCardData.estAiLevel,
      carClass: raceCardData.carClass,
      qualifyingFormat: raceCardData.qualifyingFormat,
      startTime: raceCardData.startTime,
      raceDate: raceCardData.raceDate,
      fullTimeSeat: raceCardData.fullTimeSeat,
      discordLink: raceCardData.discordLink || '',
    };

    const applicationData = {
      raceCardId,
      applicantId,
      applicantEaId,
      cardCreatorEaId, // Store the card creator's EA ID
      cardData, // Store the card data for display
      status: 'pending', // pending, accepted, rejected
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'raceCardApplications'), applicationData);
    const application = { id: docRef.id, ...applicationData };

    // Create notification for the card creator
    const cardCreatorId = raceCardData.userId;
    if (cardCreatorId && cardCreatorId !== applicantId) {
      try {
        await createNotification(
          cardCreatorId,
          'application_received',
          `${applicantEaId} has applied to your race card: ${raceCardData.title || 'Untitled Card'}`,
          {
            raceCardId,
            applicationId: docRef.id,
            applicantEaId
          }
        );
      } catch (notifError) {
        // Don't fail the application if notification creation fails
        console.error('Error creating notification:', notifError);
      }
    }

    return application;
  } catch (error) {
    console.error('Error applying to race card:', error);
    throw error;
  }
};

export const getApplicationsForRaceCard = (raceCardId, callback) => {
  try {
    const q = query(
      collection(db, 'raceCardApplications'),
      where('raceCardId', '==', raceCardId)
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        try {
          const applications = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          applications.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return b.createdAt.toDate() - a.createdAt.toDate();
            }
            return 0;
          });
          callback(applications);
        } catch (error) {
          console.error('Error processing applications:', error);
          callback([]);
        }
      },
      (error) => {
        console.error('Error in applications listener:', error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up applications listener:', error);
    callback([]);
    return () => {};
  }
};

export const updateApplicationStatus = async (applicationId, status) => {
  try {
    // Get the application data first to get applicant info
    const applicationRef = doc(db, 'raceCardApplications', applicationId);
    const applicationDoc = await getDoc(applicationRef);
    
    if (!applicationDoc.exists()) {
      throw new Error('Application not found.');
    }

    const applicationData = applicationDoc.data();

    // Update the application status
    await updateDoc(applicationRef, {
      status,
      updatedAt: serverTimestamp()
    });

    // Create notification for the applicant if application was accepted or rejected
    if ((status === 'accepted' || status === 'rejected') && applicationData.applicantId) {
      try {
        // Get the race card to get the card title
        const raceCardRef = doc(db, RACE_CARDS_COLLECTION, applicationData.raceCardId);
        const raceCardDoc = await getDoc(raceCardRef);
        const raceCardPayload = raceCardDoc.exists() ? raceCardDoc.data() : {};
        const cardTitle = raceCardDoc.exists()
          ? (raceCardPayload.title || 'Untitled Card')
          : (applicationData.cardData?.title || 'Untitled Card');

        const notificationType = status === 'accepted' ? 'application_accepted' : 'application_rejected';
        const cardCreatorEaId =
          applicationData.cardCreatorEaId ||
          raceCardPayload.createdByEaId ||
          'The host';

        let notificationMessage =
          status === 'accepted'
            ? `Your application has been accepted for: ${cardTitle}. ${cardCreatorEaId} will add you on EA.`
            : `Your application has been rejected for: ${cardTitle}`;

        const discordRaw = String(
          (raceCardPayload.discordLink || applicationData.cardData?.discordLink || '')
        ).trim();

        const notificationPayload = {
          raceCardId: applicationData.raceCardId,
          applicationId,
        };

        if (status === 'accepted' && discordRaw) {
          notificationPayload.discordLink = discordRaw;
        }

        await createNotification(
          applicationData.applicantId,
          notificationType,
          notificationMessage,
          notificationPayload
        );
      } catch (notifError) {
        // Don't fail the status update if notification creation fails
        console.error('Error creating notification:', notifError);
      }
    }
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

export const deleteApplicationForRaceCard = async (raceCardId, applicantId) => {
  try {
    const q = query(
      collection(db, 'raceCardApplications'),
      where('raceCardId', '==', raceCardId),
      where('applicantId', '==', applicantId),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const docId = snapshot.docs[0].id;
      await deleteDoc(doc(db, 'raceCardApplications', docId));
    }
  } catch (error) {
    console.error('Error deleting application:', error);
    throw error;
  }
};

// Applications older than this are deleted to prevent unbounded growth
const RACE_CARD_APPLICATION_EXPIRY_MONTHS = 6;

/**
 * Deletes race card applications older than 6 months. Safe to call repeatedly.
 * Uses batched writes (max 500 per batch). Returns total number of applications deleted.
 */
export const cleanupExpiredRaceCardApplications = async () => {
  try {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - RACE_CARD_APPLICATION_EXPIRY_MONTHS);
    const cutoffTimestamp = Timestamp.fromDate(cutoff);

    const BATCH_SIZE = 500; // Firestore batch limit
    let totalDeleted = 0;

    // Keep fetching and deleting until no more expired docs (handles >500 at once)
    let hasMore = true;
    while (hasMore) {
      const q = query(
        collection(db, 'raceCardApplications'),
        where('createdAt', '<', cutoffTimestamp),
        limit(BATCH_SIZE)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      for (const docSnap of snapshot.docs) {
        batch.delete(doc(db, 'raceCardApplications', docSnap.id));
        totalDeleted++;
      }
      await batch.commit();
      if (snapshot.docs.length < BATCH_SIZE) hasMore = false;
    }

    return totalDeleted;
  } catch (error) {
    console.error('Error cleaning up expired race card applications:', error);
    throw error;
  }
};

// Utility function to clean up orphaned race cards (for debugging)
export const cleanupOrphanedCards = async () => {
  try {
    console.log('Starting cleanup of orphaned race cards...');
    
    // Get all active cards
    const q = query(
      collection(db, RACE_CARDS_COLLECTION),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    
    let deletedCount = 0;
    for (const doc of snapshot.docs) {
      const data = doc.data();
      console.log('Checking card:', doc.id, 'userId:', data.userId);
      
      // Delete cards without userId
      if (!data.userId) {
        console.log('Deleting orphaned card without userId:', doc.id);
        await deleteDoc(doc.ref);
        deletedCount++;
      }
    }
    
    console.log(`Cleanup complete. Deleted ${deletedCount} orphaned cards.`);
    return deletedCount;
  } catch (error) {
    console.error('Error during cleanup:', error);
    throw error;
  }
}; 